"use client";

import { useEffect, useState } from "react";
type Region = {
  region: number;
  lat: number;
  lon: number;
  mag: string;
  area: number;
};
const SWPC_URL = "https://services.swpc.noaa.gov/json/solar_regions.json";
const SCX = 50;
const SCY = 50;
const SR = 46;
const GREEN = "#4ade80";
const RED = "#f87171";
function classColor(mag: string): string {
  const s = (mag || "").toLowerCase();
  if (s.includes("delta")) return "var(--destructive)";
  if (s.includes("gamma")) return "var(--corona-orange)";
  if (s.includes("beta")) return "var(--photon-blue)";
  return "var(--muted-foreground)";
}
function parseLocation(loc: string): {
  lat: number;
  lon: number;
} | null {
  const m = (loc || "").match(/([NS])(\d+)([EW])(\d+)/);
  if (!m) return null;
  return {
    lat: (m[1] === "S" ? -1 : 1) * Number(m[2]),
    lon: (m[3] === "E" ? -1 : 1) * Number(m[4])
  };
}
function project(lat: number, lon: number) {
  const la = lat * Math.PI / 180;
  const lo = lon * Math.PI / 180;
  return {
    px: SCX + Math.cos(la) * Math.sin(lo) * SR,
    py: SCY - Math.sin(la) * SR
  };
}
function visibility(lat: number, lon: number): number {
  return Math.max(0, Math.cos(lat * Math.PI / 180) * Math.cos(lon * Math.PI / 180));
}
function dotR(area: number): number {
  return Math.max(1.8, Math.min(5, 1.8 + Math.sqrt(area || 0) / 9));
}
export default function SolarDiskStrip() {
  const [regions, setRegions] = useState<Region[]>([]);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const res = await fetch(SWPC_URL, {
          cache: "no-store"
        });
        if (!res.ok) throw new Error("http");
        const raw: any[] = await res.json();
        if (!Array.isArray(raw) || raw.length === 0) throw new Error("empty");
        const latest = raw.reduce((a, r) => r.observed_date > a ? r.observed_date : a, raw[0].observed_date);
        const parsed: Region[] = raw.filter(r => r.observed_date === latest).map(r => {
          const p = parseLocation(r.location);
          const lat = p ? p.lat : Number(r.latitude);
          const lon = p ? p.lon : Number(r.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
          return {
            region: Number(r.region),
            lat,
            lon,
            mag: r.mag_class || "",
            area: Number(r.area) || 0
          } as Region;
        }).filter((r): r is Region => r !== null);
        if (!alive) return;
        setRegions(parsed);
        setStatus("ok");
      } catch {
        if (alive) setStatus("error");
      }
    }
    load();
    const id = setInterval(load, 30 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);
  if (status === "error") return null;
  const deltaCount = regions.filter(r => r.mag.toLowerCase().includes("delta")).length;
  const largest = regions.reduce<Region | null>((m, r) => r.area > (m?.area ?? -1) ? r : m, null);
  const pad = (n: number) => String(n).padStart(2, "0");
  return <section id="solar-strip" className="w-full border-y border-secondary/20 bg-background text-foreground ![margin:47px_0px_47px_0px]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-6 flex flex-col sm:flex-row items-center gap-6 sm:gap-8" style={{
      backgroundColor: "rgba(15, 17, 21, 0.4)",
      backdropFilter: "blur(24px)"
    }}>
        {/* Mini disk */}
        <div className="shrink-0">
          <svg viewBox="0 0 100 100" width={88} height={88} role="img" aria-label="Live solar disk, active regions">
            <defs>
              <radialGradient id="stripLimb" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFA94D" stopOpacity={0.18} />
                <stop offset="60%" stopColor="#F97316" stopOpacity={0.07} />
                <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
              </radialGradient>
            </defs>
            <circle cx={SCX} cy={SCY} r={SR} fill="url(#stripLimb)" stroke="var(--border)" strokeWidth={1} />
            {status === "ok" && regions.map(r => {
            const {
              px,
              py
            } = project(r.lat, r.lon);
            return <circle key={r.region} cx={px} cy={py} r={dotR(r.area)} fill={classColor(r.mag)} opacity={0.35 + 0.65 * visibility(r.lat, r.lon)} />;
          })}
          </svg>
        </div>

        {/* Label + stats */}
        <div className="flex-1 w-full flex flex-col gap-3 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <span className="size-1.5 animate-pulse" style={{
            backgroundColor: deltaCount > 0 ? RED : GREEN
          }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--corona-orange)]">
              LIVE FROM SWPC · ACTIVE REGIONS
            </span>
          </div>

          {status === "loading" ? <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/30 animate-pulse">
              ACQUIRING DISK…
            </span> : <div className="flex flex-wrap items-baseline justify-center sm:justify-start gap-x-6 gap-y-2 font-mono">
              <span className="flex items-baseline gap-1.5">
                <span className="text-foreground font-bold text-lg tabular-nums">{pad(regions.length)}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/40">NUMBERED</span>
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="font-bold text-lg tabular-nums" style={{
              color: deltaCount > 0 ? RED : "var(--foreground)"
            }}>
                  {pad(deltaCount)}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/40">FLARE-CAPABLE</span>
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="text-foreground font-bold text-lg tabular-nums">
                  {largest && largest.area > 0 ? `AR${largest.region}` : "—"}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/40">LARGEST</span>
              </span>
            </div>}
        </div>

        {/* CTA */}
        <div className="shrink-0">
          <a href="/solar" className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/70 hover:text-foreground transition-colors">
            <span className="border-b border-[var(--corona-orange)]/40 group-hover:border-[var(--corona-orange)] pb-1 transition-colors">FULL DISK</span>
            <span className="text-[var(--corona-orange)] transition-transform duration-300 group-hover:translate-x-0.5">→</span>
          </a>
        </div>
      </div>
    </section>;
}